import type { AtmosphereQuality } from "../engine/atmosphere.js";

export const PRECIPITATION_BUDGETS = Object.freeze({ low: 384, medium: 896, high: 1536 });
export const LIGHTNING_LIFETIME_MS = 650;
export interface VisualLightningEvent {
  readonly id: string;
  readonly serverTime: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly seed: number;
  readonly intensity: number;
}
export interface PrecipitationFrame {
  readonly rain: number;
  readonly snow: number;
  readonly hail: number;
  readonly windSpeed: number;
  readonly windDirectionRadians: number;
  readonly daylight: number;
}

/** A single total cap applies even while three precipitation kinds crossfade. */
export function precipitationCounts(frame: PrecipitationFrame, quality: AtmosphereQuality): readonly [number, number, number] {
  const values = [frame.rain, frame.snow, frame.hail].map((v) => Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0);
  const budget = PRECIPITATION_BUDGETS[quality];
  const denominator = Math.max(1, values[0]! + values[1]! + values[2]!);
  return values.map((value) => Math.floor(budget * value / denominator)) as [number, number, number];
}

function seededRandom(seed: number): () => number {
  let state = seed >>> 0 || 0x6d2b79f5;
  return () => {
    state ^= state << 13; state ^= state >>> 17; state ^= state << 5;
    return (state >>> 0) / 4294967296;
  };
}

/** Lines in world space. The same server seed makes the same bolt for every viewer. */
export function lightningSegments(event: VisualLightningEvent): Float32Array {
  const random = seededRandom(event.seed);
  const segments: number[] = [];
  const top = event.y + 62 + random() * 24;
  let previous = [event.x + (random() - 0.5) * 14, top, event.z + (random() - 0.5) * 14];
  const main: number[][] = [previous];
  for (let step = 1; step <= 24; step++) {
    const height = 1 - step / 24;
    const next = [event.x + (random() - 0.5) * 9 * height, event.y + (top - event.y) * height, event.z + (random() - 0.5) * 9 * height];
    segments.push(...previous, ...next); main.push(next); previous = next;
  }
  for (let branch = 0; branch < 2; branch++) {
    previous = main[7 + branch * 6]!;
    const direction = random() * Math.PI * 2;
    for (let step = 0; step < 6; step++) {
      const next = [previous[0]! + Math.cos(direction) * 2 + random() - 0.5, previous[1]! - 2.4, previous[2]! + Math.sin(direction) * 2 + random() - 0.5];
      segments.push(...previous, ...next); previous = next;
    }
  }
  return new Float32Array(segments);
}

export function lightningFlashAt(event: VisualLightningEvent, serverEpoch: number): number {
  const age = serverEpoch - event.serverTime;
  if (age < 0 || age >= LIGHTNING_LIFETIME_MS) return 0;
  // Two quick pulses followed by a soft afterglow, all anchored to server time.
  return event.intensity * (Math.exp(-age / 72) + (age >= 125 ? 0.62 * Math.exp(-(age - 125) / 58) : 0));
}

export class LightningPresentation {
  readonly active: { event: VisualLightningEvent; segments: Float32Array }[] = [];
  private readonly seen = new Set<string>();
  received = 0;
  ingest(event: VisualLightningEvent, serverEpoch: number): void {
    if (this.seen.has(event.id) || serverEpoch - event.serverTime > LIGHTNING_LIFETIME_MS || event.serverTime - serverEpoch > 2000) return;
    this.seen.add(event.id);
    if (this.seen.size > 128) this.seen.delete(this.seen.values().next().value!);
    if (this.active.length === 4) this.active.shift();
    this.active.push({ event, segments: lightningSegments(event) }); this.received++;
  }
  update(serverEpoch: number): number {
    for (let i = this.active.length - 1; i >= 0; i--) {
      if (serverEpoch - this.active[i]!.event.serverTime >= LIGHTNING_LIFETIME_MS) this.active.splice(i, 1);
    }
    return Math.min(1.8, this.active.reduce((sum, item) => sum + lightningFlashAt(item.event, serverEpoch), 0));
  }
}
